// Full service catalogue. Flagship services have rich detail pages.
export const SERVICES = [
  {
    slug: "generative-engine-optimization",
    title: "Generative Engine Optimization",
    short: "The complete GEO program to get your brand cited and recommended by AI engines.",
    icon: "Sparkles",
    featured: true,
    keyword: "Generative Engine Optimization Services",
    overview: [
      "Generative Engine Optimization (GEO) is the discipline of making your brand discoverable, understandable, trustworthy and citable to AI-powered search engines and large language models. As buyers shift from typing queries into Google to asking ChatGPT, Gemini, Claude and Perplexity, the brands that AI chooses to recommend win the market.",
      "CompuBrain AI runs an end-to-end GEO program that combines strategy, technical implementation and ongoing optimization. We engineer your content, structured data, brand entities and digital authority so AI systems can parse your facts, trust your brand and cite you in the answers your customers see.",
      "This is not traditional SEO with a new name. GEO optimizes for entities and answers rather than keywords and blue links. Our framework is built for the reality that most AI answers are synthesized, sourced from trusted entities, and delivered without a click to your website.",
    ],
    benefits: [
      "Appear in AI-generated answers where your buyers now research",
      "Increase citations and brand mentions across ChatGPT, Gemini, Claude and Perplexity",
      "Build durable entity authority that compounds over time",
      "Protect your brand from inaccurate AI descriptions",
      "Win share of voice against competitors in AI search",
      "Turn AI visibility into qualified inbound enquiries",
    ],
    deliverables: [
      "Full AI visibility audit and competitive benchmark",
      "Prioritised GEO roadmap mapped to business outcomes",
      "Structured data & schema implementation",
      "Entity and knowledge-graph optimization",
      "AI-ready content engineering and answer blocks",
      "Authority and citation building program",
      "Monthly monitoring, reporting and optimization",
    ],
    faqs: [
      { q: "What does a GEO program include?", a: "A typical GEO program includes an AI visibility audit, a prioritised strategy, technical implementation (schema, entities, content), authority building and continuous monitoring and optimization." },
      { q: "How is GEO measured?", a: "We track AI citations, brand mentions inside answers, share of AI voice versus competitors, and downstream qualified enquiries." },
      { q: "How soon will I see results?", a: "Foundational improvements can influence AI answers within weeks; durable entity authority builds over a few months of consistent work." },
    ],
  },
  {
    slug: "ai-visibility-audit",
    title: "AI Visibility Audit",
    short: "Discover exactly how ChatGPT, Gemini, Claude and Perplexity see and cite your brand today.",
    icon: "ScanSearch",
    featured: true,
    keyword: "AI Visibility Audit Service",
    overview: [
      "You cannot improve what you cannot see. The AI Visibility Audit is a rigorous diagnostic that reveals precisely how today's leading AI engines discover, describe, trust and cite your brand — and where competitors are winning the answers you should own.",
      "Our analysts run structured prompt testing across ChatGPT, Google AI Mode, Gemini, Claude and Perplexity, mapping how each engine represents your brand entity, which sources it trusts, and whether it recommends you at the moment of decision.",
      "You receive a clear, prioritised report: your current AI visibility score, category-by-category strengths and gaps, competitor benchmarking, and a step-by-step roadmap of the highest-impact fixes to become citable.",
    ],
    benefits: [
      "See your real AI visibility score and grade",
      "Understand which engines cite you — and which ignore you",
      "Benchmark against direct competitors",
      "Identify quick wins and strategic priorities",
      "Get a defensible, board-ready visibility baseline",
    ],
    deliverables: [
      "AI visibility score across six GEO categories",
      "Prompt-level analysis across major AI engines",
      "Competitor citation benchmark",
      "Entity and structured-data gap analysis",
      "Prioritised remediation roadmap",
    ],
    faqs: [
      { q: "Is the audit a one-time service?", a: "Yes, it can be a standalone one-time diagnostic, or the first phase of a full GEO program." },
      { q: "Can I try a quick version first?", a: "Yes — use our free AI Visibility Checker tool for an instant snapshot, then request the full audit for depth." },
    ],
  },
  {
    slug: "entity-seo",
    title: "Entity SEO & Knowledge Graph",
    short: "Establish a strong, consistent brand entity that AI systems recognise and trust.",
    icon: "Network",
    featured: true,
    keyword: "Entity SEO Services",
    overview: [
      "AI engines reason about entities — people, brands, products and concepts — not just keywords. Entity SEO makes your brand a well-defined, consistently described entity that AI systems can confidently recognise, connect and recommend.",
      "We map your entity across the web, resolve inconsistencies, strengthen your knowledge-graph presence (including sources like Wikidata and authoritative profiles), and build the semantic relationships that make your brand unambiguous to machines.",
      "The result is entity authority: the trust signal that increasingly determines which brands AI chooses to cite.",
    ],
    benefits: [
      "Become a recognised, trusted entity in AI knowledge graphs",
      "Eliminate conflicting information that confuses AI",
      "Strengthen semantic relationships around your brand",
      "Improve accuracy of how AI describes you",
      "Build a defensible authority moat",
    ],
    deliverables: [
      "Entity audit and disambiguation",
      "Knowledge-graph and Wikidata optimization",
      "Consistent entity data (NAP, sameAs, profiles)",
      "Entity home page and topical entity clusters",
      "Ongoing entity monitoring",
    ],
    faqs: [
      { q: "What is entity authority?", a: "It is how strongly AI recognises your brand as a distinct, trustworthy entity, based on consistent data, knowledge-graph presence and credible references." },
    ],
  },
  {
    slug: "technical-geo",
    title: "Technical GEO",
    short: "The technical foundation that makes your site machine-readable and citable by AI.",
    icon: "Cpu",
    featured: true,
    keyword: "Technical GEO Service",
    overview: [
      "Technical GEO ensures AI crawlers and reasoning systems can access, parse and reuse your content without friction. Many brands lose AI visibility not because of weak content, but because of technical barriers that make their information hard for machines to read.",
      "We optimize crawlability for AI bots, implement clean semantic HTML, structure your content into machine-readable answer units, and align your technical foundation with how LLMs ingest and cite information.",
      "This is the plumbing of GEO — invisible to users, decisive for AI.",
    ],
    benefits: [
      "Ensure AI crawlers can access and understand your content",
      "Serve clean, semantic, machine-readable markup",
      "Improve answer-unit structure for citation",
      "Fix technical barriers hurting AI visibility",
    ],
    deliverables: [
      "AI crawlability and robots optimization",
      "Semantic HTML and heading hierarchy",
      "Answer-unit content structuring",
      "Core Web Vitals and performance tuning",
      "Technical GEO monitoring",
    ],
    faqs: [
      { q: "Do you optimize for AI crawlers specifically?", a: "Yes. We configure access for GPTBot, PerplexityBot, ClaudeBot, Google-Extended and other AI crawlers, alongside standard SEO technical health." },
    ],
  },
  {
    slug: "schema-implementation",
    title: "Structured Data & Schema Implementation",
    short: "JSON-LD schema that turns your facts into machine-readable signals AI can trust.",
    icon: "Braces",
    featured: true,
    keyword: "Structured Data Implementation Service",
    overview: [
      "Structured data is the language AI uses to understand your facts without ambiguity. Comprehensive, accurate JSON-LD schema makes your organization, services, FAQs, articles and reviews machine-readable — dramatically increasing the odds of accurate citation.",
      "We design and implement a complete schema architecture: Organization, LocalBusiness, Service, FAQPage, Breadcrumb, WebSite, WebPage and more — validated and maintained as your site evolves.",
      "Clean structured data reduces the risk of AI misrepresenting your brand and increases the trust signals that drive recommendation.",
    ],
    benefits: [
      "Make your facts unambiguous to AI systems",
      "Increase eligibility for citation and rich results",
      "Reduce AI misrepresentation of your brand",
      "Future-proof your content for evolving schema standards",
    ],
    deliverables: [
      "Complete JSON-LD schema architecture",
      "Organization, Service, FAQ, Breadcrumb & WebSite schema",
      "Schema validation and error resolution",
      "Ongoing schema maintenance",
    ],
    faqs: [
      { q: "Which schema types matter most for GEO?", a: "Organization, LocalBusiness, Service, FAQPage, Article, Breadcrumb and WebSite/SearchAction are foundational for AI understanding and citation." },
    ],
  },
  // Additional catalogue services (hub cards; detail pages can be expanded later)
  { slug: "ai-search-optimization", title: "AI Search Optimization", short: "Win visibility across AI search surfaces and answer engines.", icon: "Radar", keyword: "AI Search Optimization Services" },
  { slug: "geo-consulting", title: "GEO Consulting", short: "Advisory and strategy from dedicated GEO experts.", icon: "Compass", keyword: "GEO Consultant" },
  { slug: "geo-implementation", title: "GEO Implementation", short: "Hands-on execution of your entire GEO roadmap.", icon: "Wrench", keyword: "GEO Implementation" },
  { slug: "semantic-seo", title: "Semantic SEO", short: "Topical depth and meaning that AI rewards.", icon: "Layers", keyword: "Semantic SEO Services" },
  { slug: "ai-citation-building", title: "AI Citation Building", short: "Earn the authoritative citations AI engines trust.", icon: "Quote", keyword: "AI Citation Building Service" },
  { slug: "ai-content-engineering", title: "AI Content Engineering", short: "Answer-first content designed to be quoted by AI.", icon: "PenTool", keyword: "AI Content Optimization" },
  { slug: "ai-authority-building", title: "AI Authority Building", short: "Build the trust signals that make AI recommend you.", icon: "ShieldCheck", keyword: "AI Authority Building Service" },
  { slug: "topical-authority-development", title: "Topical Authority Development", short: "Own your category with comprehensive topic clusters.", icon: "GitBranch", keyword: "Topical Authority Development Service" },
];

export const FEATURED_SERVICES = SERVICES.filter((s) => s.featured);
export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
